import { OpenInNewOutlined } from "@mui/icons-material";
import { CircularProgress, IconButton } from "@mui/material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  formatHelpTopicLabel,
  HELP_TOPIC_DRAWER_WIDTH_PX,
  IHelpTopicDrawerProps,
  IInjectedDrawerProps,
  resolveHelpTopicAppRoute,
} from "@ryde/shared";

import {
  DrawerCloseIconButton,
  DrawerContentStyled,
} from "../drawers/components/drawer";
import {
  HelpTopicDrawerBackdropStyled,
  HelpTopicDrawerHeaderStyled,
  helpTopicDrawerMotionVariants,
  HelpTopicDrawerPanelStyled,
  HelpTopicDrawerTitleStyled,
  HelpTopicDrawerToolbarStyled,
} from "./help-topic-drawer.styled";
import { HelpTopicLanguageSwitcher } from "./help-topic-language-switcher";
import { HelpTopicMarkdown } from "./help-topic-markdown";
import { useHelpTopicContent } from "./use-help-topic-content";

export const HelpTopicDrawer: FC<
  IHelpTopicDrawerProps & IInjectedDrawerProps
> = ({ helpTopikUrl, topicTitle, onCloseDrawer }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    markdown,
    markdownBasePath,
    isLoading,
    error,
    locale,
    hasHebrewTranslation,
    setLocale,
  } = useHelpTopicContent(helpTopikUrl);

  const resolvedTitle =
    topicTitle ||
    formatHelpTopicLabel(helpTopikUrl.split("/").pop() ?? helpTopikUrl);

  const openFullPage = (): void => {
    navigate(resolveHelpTopicAppRoute(helpTopikUrl));
    onCloseDrawer();
  };

  return (
    <HelpTopicDrawerBackdropStyled onClick={onCloseDrawer}>
      <HelpTopicDrawerPanelStyled
        customWidth={`${HELP_TOPIC_DRAWER_WIDTH_PX}px`}
        variants={helpTopicDrawerMotionVariants}
        initial="initial"
        animate="animation"
        exit="exit"
        onClick={event => event.stopPropagation()}
      >
        <HelpTopicDrawerHeaderStyled>
          <HelpTopicDrawerTitleStyled>
            {resolvedTitle}
          </HelpTopicDrawerTitleStyled>
          <HelpTopicDrawerToolbarStyled>
            <HelpTopicLanguageSwitcher
              locale={locale}
              hasHebrewTranslation={hasHebrewTranslation}
              onLocaleChange={setLocale}
            />
            <IconButton
              size="small"
              onClick={openFullPage}
              aria-label={t("helpTopics.openFullPage", {
                defaultValue: "Open full page",
              })}
            >
              <OpenInNewOutlined fontSize="small" />
            </IconButton>
            <DrawerCloseIconButton onCloseDrawer={onCloseDrawer} />
          </HelpTopicDrawerToolbarStyled>
        </HelpTopicDrawerHeaderStyled>

        <DrawerContentStyled className="help-topic-drawer-content">
          {isLoading && (
            <div className="HelpTopicDrawer-Loading">
              <CircularProgress size={28} />
            </div>
          )}
          {!isLoading && error && (
            <p className="HelpTopicDrawer-Error" role="alert">
              {error}
            </p>
          )}
          {!isLoading && !error && markdown && (
            <HelpTopicMarkdown
              key={`${locale}-${markdownBasePath}`}
              markdown={markdown}
              markdownBasePath={markdownBasePath}
              dir={locale === "he" ? "rtl" : "ltr"}
            />
          )}
        </DrawerContentStyled>
      </HelpTopicDrawerPanelStyled>
    </HelpTopicDrawerBackdropStyled>
  );
};
